// src/components/dashboard/PlanCard/PlanCard.jsx - PRODUCTION MODE
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './PlanCard.css';

const PlanCard = ({ plan }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/withdraw/plan/${plan.id}`);
  };

  const formatPrice = (price) => {
    const currency = plan.display_currency || 'NGN';
    const numAmount = parseFloat(price) || 0;
    
    if (currency === 'NGN') {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numAmount);
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numAmount);
    }
  };

  const getProgressPercentage = () => {
    const now = new Date();
    const startDate = new Date(plan.lock_start_date);
    const endDate = new Date(plan.lock_end_date);
    
    const totalDuration = endDate - startDate;
    const elapsedDuration = now - startDate;
    
    if (totalDuration <= 0) return 100;
    if (elapsedDuration <= 0) return 0;
    
    const percentage = (elapsedDuration / totalDuration) * 100;
    return Math.min(Math.max(percentage, 0), 100);
  };

  const getDaysRemaining = () => {
    const now = new Date();
    const endDate = new Date(plan.lock_end_date);
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(diffDays, 0);
  };

  const getMaturityTimeDisplay = () => {
    const daysRemaining = getDaysRemaining();
    
    if (daysRemaining === 0) {
      return 'Matured';
    }
    
    if (daysRemaining === 1) {
      return '1 day remaining';
    }
    
    if (daysRemaining < 7) {
      return `${daysRemaining} days remaining`;
    }
    
    if (daysRemaining < 365) {
      const weeks = Math.floor(daysRemaining / 7);
      const remainingDays = daysRemaining % 7;
      if (remainingDays === 0) {
        return `${weeks} week${weeks !== 1 ? 's' : ''} remaining`;
      }
      return `${weeks}w ${remainingDays}d remaining`;
    }
    
    const years = (daysRemaining / 365).toFixed(1);
    return `${years} year${parseFloat(years) !== 1 ? 's' : ''} remaining`;
  };

  const getNextGrowthDisplay = () => {
    if (!plan.next_growth_date) return 'N/A';
    
    const now = new Date();
    const nextGrowth = new Date(plan.next_growth_date);
    const diffDays = Math.ceil((nextGrowth - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) {
      return 'Due now';
    }
    
    if (diffDays === 1) {
      return 'Tomorrow';
    }
    
    if (diffDays < 7) {
      return `In ${diffDays} days`;
    }
    
    return nextGrowth.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isMatured = plan.is_matured || getDaysRemaining() === 0;
  const progressPercentage = getProgressPercentage();
  const daysRemaining = getDaysRemaining();
  const isGrowthDue = plan.is_growth_due || false;

  console.log('📊 PlanCard display:', {
    plan_name: plan.plan_name,
    display_currency: plan.display_currency,
    current_balance: plan.current_balance,
    is_growth_due: isGrowthDue,
    next_growth_date: plan.next_growth_date,
    days_remaining: daysRemaining
  });

  return (
    <div 
      className={`plan-card ${isMatured ? 'matured' : ''}`}
      onClick={handleCardClick}
    >
      <div className="plan-card-header">
        <h3 className="plan-name">{plan.plan_name}</h3>
        <span className={`plan-status ${isMatured ? 'matured' : 'active'}`}>
          {isMatured ? 'Matured' : 'Active'}
        </span>
      </div>

      <div className="plan-card-body">
        <div className="plan-amounts">
          <div className="amount-item">
            <span className="label">Initial Investment:</span>
            <span className="value">{formatPrice(plan.initial_investment_amount)}</span>
          </div>
          <div className="amount-item">
            <span className="label">Current Balance:</span>
            <span className="value">{formatPrice(plan.current_balance)}</span>
            {isGrowthDue && !isMatured && (
              <span className="growth-due-badge">📈 Growth Due!</span>
            )}
          </div>
          <div className="amount-item">
            <span className="label">Next Projected Return:</span>
            <span className="value">{formatPrice(plan.weekly_growth_amount || 0)}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="progress-section">
          <div className="progress-info">
            <span className="progress-text">
              {getMaturityTimeDisplay()}
            </span>
            <span className="progress-percentage">{Math.round(progressPercentage)}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>

        {/* Lock Period Info */}
        <div className="lock-info">
          <span className="lock-icon">🔒</span>
          <span className="lock-text">
            {isMatured ? 'Unlocked' : `Locked - ${getMaturityTimeDisplay()}`}
          </span>
        </div>

        {/* Growth Status */}
        {!isMatured && plan.next_growth_date && (
          <div className="growth-info">
            <span className="growth-icon">📈</span>
            <span className="growth-text">
              Next return: {getNextGrowthDisplay()}
            </span>
          </div>
        )}
      </div>

      <div className="plan-card-footer">
        <button 
          className={`withdraw-btn ${isMatured ? 'active' : 'disabled'}`}
          disabled={!isMatured}
        >
          {isMatured ? 'Withdraw Funds' : 'Locked'}
        </button>
      </div>
    </div>
  );
};

export default PlanCard;